import React, { useState, useEffect, useRef } from 'react';
import { FactProcess, TranscriptEntry, ProcessStatus, TwilioCallStatus } from '../types';
import { getNextQuestion, generateSpeech } from '../services/geminiService';
import { AudioPlayer } from '../services/audioService';
import { NotificationService } from '../services/notificationService';
import { TwilioService } from '../services/twilioService';
import { geminiLive } from '../services/geminiLiveService';

interface VoiceInterviewProps {
  intake: FactProcess;
  onUpdate: (updatedIntake: FactProcess) => void;
  onClose: () => void;
}

/**
 * Victoria-1 Voice & Text Consultation Interface.
 * Orchestrates telephony (Twilio) and high-reasoning AI (Gemini).
 */
const VoiceInterview: React.FC<VoiceInterviewProps> = ({ intake, onUpdate, onClose }) => {
  const isTelephonic = intake.appointment.method === 'Telephonic';
  const [hasVerifiedIdentity, setHasVerifiedIdentity] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [clientInput, setClientInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPausingFlow, setIsPausingFlow] = useState(false);
  const [pauseRescheduleTime, setPauseRescheduleTime] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  
  const [callStatus, setCallStatus] = useState<TwilioCallStatus>(TwilioCallStatus.IDLE);
  const [callDuration, setCallDuration] = useState(0);
  const callIntervalRef = useRef<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const stopAudioRef = useRef<(() => void) | null>(null);

  // Buffer for live transcription updates
  const liveTranscriptBuffer = useRef<{ AI: string, Client: string }>({ AI: '', Client: '' });

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = intake.client.language === 'Spanish' ? 'es-ES' : 'en-US';

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setClientInput(transcript);
        setIsRecording(false);
      };

      recognition.onerror = () => setIsRecording(false);
      recognition.onend = () => setIsRecording(false);
      recognitionRef.current = recognition;
    }

    return () => {
      if (callIntervalRef.current) clearInterval(callIntervalRef.current);
      geminiLive.stopSession();
    };
  }, [intake.client.language]);

    // Poll transcripts from backend for telephonic calls
  useEffect(() => {
    let pollInterval: number | null = null;

    const pollTranscripts = async () => {
      if (!isTelephonic || callStatus !== TwilioCallStatus.CONNECTED) return;
      
      try {
        const response = await fetch(`/api/twilio/transcript?sessionId=${intake.id}`);
        if (response.ok) {
          const data = await response.json();
          if (data.entries && data.entries.length > 0) {
            // Convert backend entries to TranscriptEntry format
            const newEntries: TranscriptEntry[] = data.entries.map((e: any) => ({
              role: e.role,
              text: e.text,
              timestamp: e.timestamp
            }));
            
            // Only update if we have new entries
            if (newEntries.length > intake.transcript.length) {
              onUpdate({
                ...intake,
                transcript: newEntries,
                status: ProcessStatus.IN_PROGRESS
              });
            }
          }
        }
      } catch (error) {
        console.error('[Transcript Poll] Error:', error);
      }
    };

    // Start polling when call is connected
    if (isTelephonic && callStatus === TwilioCallStatus.CONNECTED) {
      pollTranscripts(); // Initial fetch
      pollInterval = window.setInterval(pollTranscripts, 2000); // Poll every 2 seconds
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [callStatus, isTelephonic, intake.id]);

  const handleStartCall = async () => {
    try {
      setApiError(null);
      setCallStatus(TwilioCallStatus.DIALING);
      
      // Pass intake data for context
      await TwilioService.initiateOutboundCall(intake.client.phone, intake.id, intake);
      
      setTimeout(() => {
        setCallStatus(TwilioCallStatus.CONNECTED);
        callIntervalRef.current = window.setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
        
        // Start real-time voice bridge locally
        startLiveVoice();
      }, 3000);
    } catch (err) {
      setCallStatus(TwilioCallStatus.FAILED);
      setApiError("Telephony Link Failure: Unable to establish outbound call via Twilio.");
      console.error('Telephony error:', err);
    }
  };

  const startLiveVoice = async () => {
    await geminiLive.startLiveSession(intake, {
      onTranscript: (text, role) => {
        // Update local buffer
        liveTranscriptBuffer.current[role] = text;
        
        // We only append to the official intake transcript when a turn is finished 
        // OR if we want to show a live preview. For recording, we'll wait for completion.
      },
      onTurnComplete: () => {
        const timestamp = new Date().toISOString();
        const entries: TranscriptEntry[] = [];
        
        if (liveTranscriptBuffer.current.Client) {
          entries.push({ role: 'Client', text: liveTranscriptBuffer.current.Client, timestamp });
        }
        if (liveTranscriptBuffer.current.AI) {
          entries.push({ role: 'AI', text: liveTranscriptBuffer.current.AI, timestamp });
        }
        
        if (entries.length > 0) {
          const updatedIntake = {
            ...intake,
            transcript: [...intake.transcript, ...entries],
            status: ProcessStatus.IN_PROGRESS
          };
          onUpdate(updatedIntake);
        }
        
        // Reset buffers
        liveTranscriptBuffer.current = { AI: '', Client: '' };
      },
      onError: (err) => {
        console.error('[Live API] Error:', err);
        setCallStatus(TwilioCallStatus.FAILED);
        setApiError("Victoria Voice Protocol Disrupted: Live session error.");
      }
    });
  };

  const formatDuration = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [intake.transcript, isProcessing, apiError]);

  const cancelAudio = () => {
    if (stopAudioRef.current) stopAudioRef.current();
    setIsSpeaking(false);
  };

  const handleSend = async (overrideInput?: string) => {
    const textToSend = (overrideInput || clientInput).trim();
    if (!textToSend || isProcessing) return;

    try {
      setApiError(null);
      setIsProcessing(true);
      cancelAudio();

      const clientEntry: TranscriptEntry = {
        role: 'Client',
        text: textToSend,
        timestamp: new Date().toISOString()
      };

      const updatedTranscriptWithClient = [...intake.transcript, clientEntry];
      const intakeWithClient = { 
        ...intake, 
        transcript: updatedTranscriptWithClient, 
        status: ProcessStatus.IN_PROGRESS 
      };
      
      onUpdate(intakeWithClient);
      setClientInput("");

      const nextQ = await getNextQuestion(intakeWithClient);
      
      if (!nextQ) throw new Error("AI returned null response.");

      const aiEntry: TranscriptEntry = {
        role: 'AI',
        text: nextQ,
        timestamp: new Date().toISOString()
      };

      const finalIntakeState = {
        ...intakeWithClient,
        transcript: [...updatedTranscriptWithClient, aiEntry]
      };

      onUpdate(finalIntakeState);

      if (isTelephonic && callStatus === TwilioCallStatus.CONNECTED) {
        // Synthesis for manual/text turn-based fallbacks
        const audioData = await generateSpeech(nextQ);
        if (audioData) {
          const buffer = await AudioPlayer.decodePCM(audioData);
          stopAudioRef.current = AudioPlayer.playBuffer(buffer, () => setIsSpeaking(false));
        }
      }

    } catch (err: any) {
      console.error("AI failure:", err);
      setApiError(`Victoria Protocol Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmPauseAndReschedule = async () => {
    cancelAudio();
    geminiLive.stopSession();
    if (pauseRescheduleTime) {
      await NotificationService.sendRescheduleNotification(intake, pauseRescheduleTime);
    }
    await NotificationService.sendPartialTranscript(intake);
    onUpdate({
      ...intake, 
      status: ProcessStatus.PAUSED,
      appointment: pauseRescheduleTime ? { ...intake.appointment, dateTime: pauseRescheduleTime } : intake.appointment
    });
    onClose();
  };

  const toggleRecording = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
    } else {
      setClientInput("");
      cancelAudio();
      setApiError(null);
      try {
        recognitionRef.current?.start();
        setIsRecording(true);
      } catch (err) { console.error(err); }
    }
  };

  if (!hasVerifiedIdentity) {
    return (
       <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in duration-300">
          <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-10 max-w-md w-full text-center shadow-2xl border border-slate-100 dark:border-slate-800 transition-colors">
             <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8 text-3xl border transition-colors ${isTelephonic ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-900/30' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/30'}`}>
                <i className={`fas ${isTelephonic ? 'fa-phone-volume' : 'fa-link'}`}></i>
             </div>
             <h3 className="text-2xl font-serif font-bold text-slate-900 dark:text-slate-100 mb-2">
               {isTelephonic ? 'Initialize Victoria Live' : 'Secure Portal Access'}
             </h3>
             <p className="text-slate-500 dark:text-slate-400 mb-8 text-sm font-medium leading-relaxed">
               {isTelephonic 
                 ? `Victoria-1 Real-time Voice Engine will dial ${intake.client.phone}. Use the monitoring deck to oversee turn-taking.` 
                 : `A secure chat link protocol has been delivered to ${intake.client.phone}. Monitor the stream below.`}
             </p>
             <div className="bg-slate-50 dark:bg-slate-950 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 mb-8 text-left">
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-2">
                  <i className="fas fa-check-circle text-emerald-500"></i>
                  Identity encryption active
                </p>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <i className="fas fa-check-circle text-emerald-500"></i>
                  Lluis Law legal privilege applied
                </p>
             </div>
             <button 
                onClick={() => {
                  setHasVerifiedIdentity(true);
                  if (isTelephonic) handleStartCall();
                  else startLiveVoice(); // Web-based live voice
                }}
                className={`w-full py-5 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all shadow-xl ${isTelephonic ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200 dark:shadow-none' : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200 dark:shadow-none'}`}
             >
                {isTelephonic ? 'Establish Voice Bridge' : 'Enter Secure Environment'}
             </button>
             <button onClick={onClose} className="w-full mt-4 py-4 text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px] tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl transition-colors">
                Discard Protocol
             </button>
          </div>
       </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-50 dark:bg-slate-950 animate-in fade-in duration-300">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-8 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-start">
            <span className="text-xl font-serif font-bold text-amber-600 dark:text-amber-500">Lluis Law</span>
            <span className="text-[7px] font-sans font-black uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500 -mt-1">Fact Gather</span>
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800"></div>
          <div>
            <h3 className="font-serif font-bold text-slate-900 dark:text-slate-100 text-sm">Victoria-1 Live Engine</h3>
            <p className="text-[9px] text-slate-400 dark:text-slate-600 font-bold uppercase tracking-widest">Matter: {intake.client.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {isTelephonic && (
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-full border border-slate-100 dark:border-slate-700">
              <span className={`w-2 h-2 rounded-full ${callStatus === TwilioCallStatus.CONNECTED ? 'bg-emerald-500 animate-pulse' : (callStatus === TwilioCallStatus.DIALING ? 'bg-amber-500 animate-pulse' : 'bg-slate-300')}`}></span>
              <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-widest">
                {callStatus === TwilioCallStatus.CONNECTED ? `CALL ACTIVE: ${formatDuration(callDuration)}` : (callStatus === TwilioCallStatus.DIALING ? 'DIALING...' : 'STANDBY')}
              </span>
            </div>
          )}
          {!isTelephonic && (
            <div className="flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-full border border-emerald-100 dark:border-emerald-800/50">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-widest">REAL-TIME BRIDGE ACTIVE</span>
            </div>
          )}
          <button onClick={onClose} className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-8 scroll-smooth">
            {intake.transcript.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                <i className="fas fa-comments text-5xl mb-4 text-slate-200 dark:text-slate-700"></i>
                <p className="text-sm font-medium text-slate-500">Awaiting consultation data stream...</p>
              </div>
            ) : (
              intake.transcript.map((entry, idx) => (
                <div key={idx} className={`flex ${entry.role === 'AI' ? 'justify-start' : 'justify-end'} animate-in fade-in`}>
                  <div className={`max-w-[80%] p-6 rounded-[2rem] shadow-sm ${entry.role === 'AI' ? 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-100 dark:border-slate-700 rounded-tl-none' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-tr-none'}`}>
                    <p className="text-[9px] font-black uppercase tracking-widest mb-2 opacity-50">{entry.role === 'AI' ? 'Victoria - Lead AI' : 'Client Response'}</p>
                    <p className="text-sm leading-relaxed font-medium whitespace-pre-wrap">{entry.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-8 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            {apiError && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-bold rounded-2xl border border-red-100 dark:border-red-900/30 flex items-center gap-3 animate-shake">
                <i className="fas fa-triangle-exclamation"></i>
                {apiError}
              </div>
            )}
            <div className="flex gap-4 items-center">
              <button 
                onClick={toggleRecording}
                className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-slate-100'}`}
              >
                <i className={`fas ${isRecording ? 'fa-stop' : 'fa-microphone'}`}></i>
              </button>
              <input 
                type="text"
                value={clientInput}
                onChange={(e) => setClientInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Direct intervention or manual message..."
                className="flex-1 px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-900 focus:border-slate-900 dark:focus:border-amber-600 outline-none transition-all font-medium"
              />
              <button 
                onClick={() => handleSend()}
                disabled={!clientInput.trim() || isProcessing}
                className="w-14 h-14 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center hover:bg-slate-800 dark:hover:bg-slate-100 transition-all disabled:opacity-30 shadow-lg shadow-slate-200 dark:shadow-none"
              >
                <i className="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="w-80 bg-slate-50/50 dark:bg-slate-950 p-8 space-y-8 flex flex-col overflow-y-auto border-l border-slate-200 dark:border-slate-800">
          <section>
            <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-4">Strategic Overview</h4>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-4">
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Focus Details</p>
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-1">{intake.client.focusDetails || 'General Declaration Intake'}</p>
              </div>
              <div className="h-px bg-slate-50 dark:bg-slate-800"></div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-tight">Language Preference</p>
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mt-1">{intake.client.language}</p>
              </div>
            </div>
          </section>

          <section className="flex-1">
            <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-4">Control Overrides</h4>
            <div className="space-y-3">
              <button 
                onClick={() => setIsPausingFlow(true)}
                className="w-full py-4 px-6 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:border-amber-600 hover:text-amber-600 transition-all flex items-center justify-between shadow-sm"
              >
                Pause Session
                <i className="fas fa-pause"></i>
              </button>
            </div>
          </section>

          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 text-center opacity-30 flex flex-col items-center">
             <span className="text-lg font-serif font-bold text-amber-600 dark:text-amber-500">Lluis Law</span>
             <span className="text-[6px] font-sans font-black uppercase tracking-[0.4em] text-slate-400 dark:text-slate-600">Fact Gather</span>
             <p className="text-[8px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-4">Victoria-1 Engine v2.5.4</p>
          </div>
        </div>
      </div>

      {isPausingFlow && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-md">
          <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-10 max-w-md w-full border border-white/10 shadow-2xl animate-in zoom-in-95 duration-200">
            <h3 className="text-2xl font-serif font-bold text-slate-900 dark:text-slate-100 mb-2">Suspend Consultation</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 font-medium leading-relaxed">Reasoning session state will be preserved. Final transcript logs delivered to firm storage.</p>
            
            <div className="mb-8">
              <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-3 ml-1">Proposed Resumption</label>
              <input 
                type="datetime-local" 
                value={pauseRescheduleTime}
                onChange={(e) => setPauseRescheduleTime(e.target.value)}
                className="w-full px-6 py-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-amber-600 outline-none transition-all dark:scheme-dark"
              />
            </div>

            <div className="flex gap-4">
              <button onClick={() => setIsPausingFlow(false)} className="flex-1 py-4 text-slate-400 font-bold uppercase text-[10px] tracking-widest hover:bg-slate-50 rounded-2xl transition-colors">Cancel</button>
              <button onClick={confirmPauseAndReschedule} className="flex-1 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold uppercase text-[10px] tracking-widest rounded-2xl hover:bg-slate-800 transition-all">Archive & Resume Later</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceInterview;
